import React from 'react';

import type { LineChart, Resolution } from '@blockscout/stats-types';
import type { StatsIntervalIds } from 'types/client/stats';

import useApiQuery from 'lib/api/useApiQuery';
import { useAppContext } from 'lib/contexts/app';
import { useI18n } from 'lib/i18n/context';
import { STATS_INTERVALS } from 'ui/stats/constants';
import { getStatsChartDescription, getStatsChartTitle } from 'ui/stats/i18n';

import formatDate from './utils/formatIntervalDate';

// 处理图表信息，将 ETH 替换为 Bot
function processChartInfo(info: LineChart['info'] | undefined, t: (key: string) => string) {
  if (!info) {
    return info;
  }

  return {
    ...info,
    title: getStatsChartTitle(info.id, info.title.replace(/ETH/g, 'Bot'), t),
    description: getStatsChartDescription(info.id, info.description.replace(/ETH/g, 'Bot'), t) || info.description.replace(/ETH/g, 'Bot'),
    units: info.units === 'ETH' ? 'Bot' : info.units,
  };
}

export default function useChartQuery(id: string, resolution: Resolution, interval: StatsIntervalIds, enabled = true) {
  const { apiData } = useAppContext<'/stats/[id]'>();
  const { t } = useI18n();

  const selectedInterval = STATS_INTERVALS[interval];

  const endDate = selectedInterval.start ? formatDate(new Date()) : undefined;
  const startDate = selectedInterval.start ? formatDate(selectedInterval.start) : undefined;

  const [ info, setInfo ] = React.useState<LineChart['info']>(apiData || undefined);

  const lineQuery = useApiQuery('stats_line', {
    pathParams: { id },
    queryParams: {
      from: startDate,
      to: endDate,
      resolution,
    },
    queryOptions: {
      enabled: enabled,
      refetchOnMount: false,
      placeholderData: {
        info: {
          title: 'Chart title placeholder',
          description: 'Chart placeholder description chart placeholder description',
          resolutions: [ 'DAY', 'WEEK', 'MONTH', 'YEAR' ],
          id: 'placeholder',
          units: undefined,
        },
        chart: [],
      },
    },
  });

  React.useEffect(() => {
    if (!info && lineQuery.data?.info && !lineQuery.isPlaceholderData) {
      // save info to keep title and description when change query params
      setInfo(lineQuery.data?.info);
    }
  }, [ info, lineQuery.data?.info, lineQuery.isPlaceholderData ]);

  const items = React.useMemo(() => lineQuery.data?.chart?.map((item) => {
    return { date: new Date(item.date), date_to: new Date(item.date_to), value: Number(item.value), isApproximate: item.is_approximate };
  }), [ lineQuery ]);

  const translatedInfo = React.useMemo(() => processChartInfo(info, t), [ info, t ]);

  return {
    items,
    info: translatedInfo,
    lineQuery,
  };
}
